import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import searchService from '../../api/searchService';
import useAuth from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';
import Avatar from '../common/Avatar';
import StarRating from '../common/StarRating';
import LoadingSpinner from '../common/LoadingSpinner';
import { currency, rating, dateOnly } from '../../utils/formatters';

export default function TechnicianDetail() {
  const { id } = useParams();
  const { role, isAuthenticated } = useAuth();
  const notify = useNotifications();
  const navigate = useNavigate();
  const [tech, setTech] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([searchService.getTechnician(id), searchService.technicianReviews(id, 0, 20)])
      .then(([t, r]) => { setTech(t); setReviews(r.content || []); })
      .catch((err) => notify.apiError(err))
      .finally(() => setLoading(false));
  }, [id, notify]);

  if (loading) return <div className="page"><div className="container"><LoadingSpinner /></div></div>;
  if (!tech) return null;

  const name = `${tech.firstName} ${tech.lastName}`;
  const canBook = role === 'CLIENT' && tech.availableForWork;

  const book = () => {
    if (!isAuthenticated) return navigate('/login', { state: { from: { pathname: `/technicians/${id}` } } });
    navigate(`/client/book/${id}`);
  };

  return (
    <div className="page">
      <div className="container split">
        <div className="card pad stack">
          <Avatar src={tech.profilePictureUrl} name={name} lg />
          <div>
            <h2>{name}</h2>
            <p className="muted">{tech.technicianTypeName}</p>
          </div>
          <div><StarRating value={tech.averageRating} /> <span className="muted">{rating(tech.averageRating)} · {tech.totalReviews || 0} reviews</span></div>
          <div className="spread"><span className="muted">Rate</span><strong>{currency(tech.hourlyRate)}/hr</strong></div>
          {tech.yearsOfExperience != null && (
            <div className="spread"><span className="muted">Experience</span><span>{tech.yearsOfExperience} yrs</span></div>
          )}
          {(tech.city || tech.state) && (
            <div className="spread"><span className="muted">Location</span><span>{[tech.city, tech.state].filter(Boolean).join(', ')}</span></div>
          )}
          {canBook ? (
            <button className="btn accent block" onClick={book}>Request a booking</button>
          ) : role === 'CLIENT' ? (
            <div className="alert warn">This technician isn’t accepting bookings right now.</div>
          ) : !isAuthenticated ? (
            <button className="btn block" onClick={book}>Log in to book</button>
          ) : null}
        </div>

        <div className="stack">
          {tech.bio && (
            <div className="card pad"><h3>About</h3><p style={{ marginTop: '.5rem' }}>{tech.bio}</p></div>
          )}
          {tech.servicesOffered?.length > 0 && (
            <div className="card pad">
              <h3>Services</h3>
              <div className="tags" style={{ marginTop: '.5rem' }}>
                {tech.servicesOffered.map((s) => <span key={s} className="badge blue">{s}</span>)}
              </div>
            </div>
          )}
          <div className="card pad">
            <h3>Reviews ({tech.totalReviews || 0})</h3>
            {reviews.length === 0 ? (
              <p className="muted" style={{ marginTop: '.5rem' }}>No reviews yet.</p>
            ) : (
              <div className="list">
                {reviews.map((r) => (
                  <div key={r.id}>
                    <div className="spread">
                      <strong>{r.clientName || 'Client'}</strong>
                      <span className="muted" style={{ fontSize: '.85rem' }}>{dateOnly(r.createdAt)}</span>
                    </div>
                    <StarRating value={r.rating} />
                    {r.comment && <p style={{ marginTop: '.25rem' }}>{r.comment}</p>}
                    {r.response && (
                      <p className="muted" style={{ marginTop: '.35rem', fontSize: '.9rem' }}>
                        <i className="bi bi-reply" /> {r.response}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Link to="/technicians" className="muted">← Back to search</Link>
        </div>
      </div>
    </div>
  );
}
